import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { getCutoffTime, groupDatesByWeek, formatWeekHeader, formatDeliveryDate, isValidDeliveryDayInWeek } from 'utils/deliveryDates';
import { COLORS, FONTS } from 'styles/theme';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { NO_MEALS_HINT } from 'utils/skippedDates';
import InfoHint from './InfoHint';
import AddDeliveryModal from './AddDeliveryModal';
const TAB_GAP = 10;
// Matches the panel below, so the open tab and the panel read as one surface.
const PAGE_CREAM = '#fffcf6';
const CARD_BEIGE = '#f3e8da';
const CARD_BEIGE_HOVER = '#ede0cf';
const BORDER_RED_SOFT = '#efb3b0';

// Same 20-minute leniency as useLockStatus.
const isPastDeadline = (date) => {
    const lock = getCutoffTime(date);
    return !!lock && Date.now() >= lock.getTime() + 20 * 60 * 1000;
};

const CLOSED_DATES_KEY = (token) => `sf_closed_delivery_dates:${token}`;

const readDateList = (key) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeDateList = (key, list) => {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* storage unavailable */ }
};

export const CalendarIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
);

export const TruckIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#2e9e5b" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
        <path d="M1 3h13v13H1z" />
        <path d="M14 8h4l4 4v4h-8z" />
        <circle cx="6" cy="18.5" r="2" fill="#fff" />
        <circle cx="18" cy="18.5" r="2" fill="#fff" />
    </svg>
);

// Skip-delivery rendering and the legend moved to MealPlanViewer (see the
// border comment there) — this component now only renders the week-header
// row and the date-tab strip itself.
const DeliveryDateTabs = ({
    deliveryDates,
    selectedDeliveryDate,
    ordersByDeliveryDate,
    deliveryStatusState,
    onDeliveryDateChange,
    panelBorderColor,
    customerDetails,
    token,
    canEdit,
    onEdit,
    onResume,
    resumingDate,
    upcomingSkips,
    onManageSkips,
    reopenRequest,
    getAddSlots,
    onAddDelivery
}) => {
    // Not persisted: after a refresh only dates with meals (or skipped ones) come back.
    const [addedDates, setAddedDates] = useState([]);
    // Tabs the customer closed. Closing only hides the tab — it never cancels meals.
    const [showAddPopup, setShowAddPopup] = useState(false);
    const [closedDates, setClosedDates] = useState(() => readDateList(CLOSED_DATES_KEY(token)));
    useEffect(() => {
        setAddedDates([]);
        setClosedDates(readDateList(CLOSED_DATES_KEY(token)));
    }, [token]);
    // Meals moved onto a closed date bring its tab back, or they'd vanish once another tab is picked
    useEffect(() => {
        const date = reopenRequest?.date;
        if (!date) return;
        setClosedDates(prev => {
            if (!prev.includes(date)) return prev;
            const next = prev.filter(d => d !== date);
            writeDateList(CLOSED_DATES_KEY(token), next);
            return next;
        });
    }, [reopenRequest, token]);

    const isPhone = useMediaQuery('(max-width: 600px)');
    const tabWidth = isPhone ? 128 : 160;
    const cardWidth = tabWidth - TAB_GAP;

    const rootRef = useRef(null);
    const scrollRef = useRef(null);
    const tabRefs = useRef({});
    const [tabRect, setTabRect] = useState({ left: 0, width: 0 });

    // Tracks the open tab (including horizontal scroll) so the panel edge below can leave a gap under it.
    useLayoutEffect(() => {
        const report = () => {
            const tab = tabRefs.current[selectedDeliveryDate];
            const root = rootRef.current;
            if (!tab || !root) return;
            const r = tab.getBoundingClientRect();
            const left = Math.round(r.left - root.getBoundingClientRect().left);
            const width = Math.round(r.width);
            setTabRect(prev => (prev.left === left && prev.width === width ? prev : { left, width }));
        };
        report();
        const scroller = scrollRef.current;
        window.addEventListener('resize', report);
        scroller?.addEventListener('scroll', report, { passive: true });
        return () => {
            window.removeEventListener('resize', report);
            scroller?.removeEventListener('scroll', report);
        };
    });

    useEffect(() => {
        const tab = tabRefs.current[selectedDeliveryDate];
        const scroller = scrollRef.current;
        if (!tab || !scroller) return;
        const t = tab.getBoundingClientRect();
        const sc = scroller.getBoundingClientRect();
        if (t.left < sc.left || t.right > sc.right) {
            scroller.scrollBy({ left: t.left - sc.left - 16, behavior: 'smooth' });
        }
    }, [selectedDeliveryDate, isPhone]);

    if (!deliveryDates) return null;

    const isAddable = (date) => isValidDeliveryDayInWeek(date)
        && (getCutoffTime(date)?.getTime() ?? 0) > Date.now();

    // Plan delivery days always show; other dates show if they have meals, are skipped, were added, or are open.
    const hasMeals = (date) => (ordersByDeliveryDate[date] || []).some(o => o.quantity > 0);
    const isPlanDay = (date) => !!deliveryStatusState[date]?.hasSubscription;
    const isVisible = (date) =>
        date === selectedDeliveryDate
        || isPlanDay(date)
        || (!closedDates.includes(date)
            && (hasMeals(date) || deliveryStatusState[date]?.isSkipped || addedDates.includes(date)));
    const visibleDates = deliveryDates.filter(isVisible);
    // Only dates the backend already schedules deliveries for (deliveryDates), on a
    // Sun-Thu delivery day, and not past their order cutoff.
    // A closed tab can always be reopened, even past its cutoff, so a delivery that
    // still has meals never becomes unreachable.
    const addableDates = deliveryDates.filter(d => !isVisible(d) && (isAddable(d) || closedDates.includes(d)));

    const handleAddDate = (date) => {
        if (!date) return;
        const next = [...new Set([...addedDates, date])].filter(d => deliveryDates.includes(d));
        const nextClosed = closedDates.filter(d => d !== date);
        setAddedDates(next);
        setClosedDates(nextClosed);
        writeDateList(CLOSED_DATES_KEY(token), nextClosed);
        onDeliveryDateChange(date);
    };

    const handleCloseTab = (date) => {
        if (visibleDates.length <= 1) return;
        const nextClosed = [...new Set([...closedDates, date])];
        const nextAdded = addedDates.filter(d => d !== date);
        setClosedDates(nextClosed);
        setAddedDates(nextAdded);
        writeDateList(CLOSED_DATES_KEY(token), nextClosed);
        if (date === selectedDeliveryDate) {
            const idx = visibleDates.indexOf(date);
            onDeliveryDateChange(visibleDates[idx + 1] || visibleDates[idx - 1]);
        }
    };

    // Group dates by week
    const datesByWeek = groupDatesByWeek(visibleDates);
    const subscriptions = customerDetails?.subscriptions || [];

    //console.log('subscriptions', subscriptions);

    // Helper function to format lock date (Date object)
    const formatLockDate = (lockDate) => {
        if (!lockDate) return '';

        return lockDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'America/New_York'
        }) + ', ' + lockDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true,
            timeZone: 'America/New_York'
        });
    };

    // Get weekly subscriptions summary
    const getWeeklySubscriptionsSummary = () => {
        // Calculate total meals by type and track premium status
        const mealsByType = subscriptions.reduce((acc, sub) => {
            const mealType = sub.isPreSetAddOn ? 'Pre-Set' : (sub.mealType || 'Meal');
            if (!acc[mealType]) {
                acc[mealType] = { count: 0, isPremium: false };
            }
            acc[mealType].count += (sub.mealsIncluded || 0);
            // Mark as premium if any subscription of this type has the premium tag
            if (sub.tags && sub.tags.includes('Premium Meat Only Paid')) {
                acc[mealType].isPremium = true;
            }
            return acc;
        }, {});

        // Get unique delivery days
        const deliveryDays = [...new Set(subscriptions.map(sub => sub.deliveryDay).filter(Boolean))];

        // Format the summary string
        const parts = Object.entries(mealsByType).map(([type, { count, isPremium }]) => {
            const pluralSuffix = count !== 1 && type !== 'Pre-Set' ? (type.toLowerCase() === 'lunch' ? 'es' : 's') : '';
            const premiumLabel = isPremium ? ' (Premium)' : '';
            return `${count} ${type.toLowerCase()}${pluralSuffix}${premiumLabel}`;
        });
        const totalSubscriptions = "Your plan";
        const mealSummary = parts.length > 0 ? parts.join(', ') : 'no active subscriptions';
        const daySummary = deliveryDays.length === 0 ? '' : `${deliveryDays.length} ${deliveryDays.length > 1 ? 'deliveries' : 'delivery'}`;

        return daySummary ? `${totalSubscriptions}: ${mealSummary}, ${daySummary}` : `${totalSubscriptions}: ${mealSummary}`;
    };

    return (
        <div ref={rootRef} style={{
            padding: '0',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            <div style={{ padding: isPhone ? '4px 12px 12px' : '8px 16px 16px', textAlign: 'center', fontFamily: FONTS.sans, color: '#5f5a55' }}>
                {Object.keys(datesByWeek).map(weekKey => (
                    <div key={weekKey} style={{ fontSize: isPhone ? '17px' : '20px', fontWeight: 700, color: '#1a1a1a', marginBottom: isPhone ? '6px' : '10px' }}>
                        {formatWeekHeader(weekKey)}
                    </div>
                ))}

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: isPhone ? '6px 16px' : '8px 28px', fontSize: isPhone ? '14px' : '16px' }}>
                    <span>{getWeeklySubscriptionsSummary()}</span>
                    {onManageSkips && (
                        <button
                            type="button"
                            onClick={onManageSkips}
                            title={upcomingSkips.length > 0 ? `Skipped: ${upcomingSkips.map(formatDeliveryDate).join(', ')}` : undefined}
                            style={{
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                font: 'inherit', fontSize: isPhone ? '14px' : '15px', fontWeight: 700, color: '#1a1a1a',
                                minHeight: '32px',
                            }}
                        >
                            <CalendarIcon size={16} />
                            <span style={{ textDecoration: 'underline', textUnderlineOffset: '4px' }}>View delivery calendar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* MealPlanViewer.css pads every button on mobile with !important. */}
            <style>{'.sf-date-tabs-scroll::-webkit-scrollbar { display: none; } .sf-tab-action { padding: 0 !important; font-size: 13px !important; }'}</style>
            <div ref={scrollRef} className="sf-date-tabs-scroll" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingLeft: isPhone ? '4px' : 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', width: 'max-content', minWidth: '100%' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'stretch',
                    }}>
                        {Object.entries(datesByWeek).map(([weekKey, datesInWeek]) => (
                            <div
                                key={weekKey}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'stretch',
                                    width: `${datesInWeek.length * tabWidth}px`,
                                }}
                            >
                                {datesInWeek.map(deliveryDate => {
                                    const isSelected = selectedDeliveryDate === deliveryDate;
                                    const orderedCount = (ordersByDeliveryDate[deliveryDate] || []).filter(o => o.quantity > 0).length;
                                    const hasOrders = orderedCount > 0;
                                    const isActuallySkipped = !!deliveryStatusState[deliveryDate]?.isSkipped;
                                    // An empty plan day looks inactive like a skip, but is labelled for what it is.
                                    const isSkipped = isActuallySkipped || (isPlanDay(deliveryDate) && !hasOrders);
                                    const pastDeadline = isPastDeadline(deliveryDate);
                                    const lockDate = getCutoffTime(deliveryDate);
                                    const canClose = !hasOrders && !isPlanDay(deliveryDate) && visibleDates.length > 1;
                                    const [weekday, ...rest] = formatDeliveryDate(deliveryDate).split(' ');
                                    const monthDay = rest.join(' ');
                                    const stackNoMeals = isPhone && isSkipped && !isActuallySkipped;

                                    // The open tab isn't clickable; only its Edit delivery link is.
                                    const Tab = isSelected ? 'div' : 'button';

                                    return (
                                        <Tab
                                            key={deliveryDate}
                                            ref={(el) => { tabRefs.current[deliveryDate] = el; }}
                                            onClick={isSelected ? undefined : () => onDeliveryDateChange(deliveryDate)}
                                            style={{

                                                flex: '0 0 auto',
                                                // Gap is carved out of tabWidth so the week widths stay in step.
                                                boxSizing: 'border-box',
                                                width: `${cardWidth}px`,
                                                marginTop: '8px',
                                                marginRight: `${TAB_GAP}px`,
                                                // Open tab drops to the panel's top edge, which leaves a gap under it.
                                                marginBottom: isSelected ? 0 : '8px',
                                                padding: isPhone
                                                    ? (isSelected ? '10px 12px 16px' : '10px 12px')
                                                    : (isSelected ? '12px 16px 20px' : '12px 16px'),
                                                borderRadius: isSelected ? '12px 12px 0 0' : '12px',
                                                backgroundColor: isSelected ? PAGE_CREAM : isSkipped ? 'transparent' : CARD_BEIGE,
                                                boxShadow: isSelected ? '0 -3px 8px rgba(0,0,0,0.06)' : 'none',
                                                color: COLORS.dark,
                                                cursor: isSelected ? 'default' : 'pointer',
                                                fontFamily: FONTS.sans,
                                                textAlign: 'left',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '4px',
                                                position: 'relative',
                                                zIndex: isSelected ? 2 : 'auto',
                                                outline: 'none',
                                                // Longhands only: React clears a removed longhand, which would unset the shorthand's edge.
                                                borderStyle: isSkipped && !isSelected ? 'dashed' : 'solid',
                                                borderWidth: isSelected ? '2px 2px 0' : '2px',
                                                borderColor: isSelected
                                                    ? panelBorderColor
                                                    : (pastDeadline ? BORDER_RED_SOFT : isSkipped ? '#cdb89f' : 'transparent'),
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isSelected && !isSkipped) {
                                                    e.currentTarget.style.backgroundColor = CARD_BEIGE_HOVER;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected && !isSkipped) {
                                                    e.currentTarget.style.backgroundColor = CARD_BEIGE;
                                                }
                                            }}
                                        >
                                            {canClose && (
                                                <span
                                                    role="button"
                                                    aria-label={`Close ${formatDeliveryDate(deliveryDate)} tab`}
                                                    title="Close tab"
                                                    onClick={(e) => { e.stopPropagation(); handleCloseTab(deliveryDate); }}
                                                    style={{ position: 'absolute', top: '6px', right: '8px', width: '18px', height: '18px', lineHeight: '16px', textAlign: 'center', borderRadius: '50%', fontSize: '14px', color: '#999999', cursor: 'pointer' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    ×
                                                </span>
                                            )}

                                            <div style={{ display: 'flex', alignItems: stackNoMeals ? 'flex-start' : 'center', justifyContent: 'space-between', columnGap: '6px', width: '100%', minHeight: '18px' }}>
                                                <span style={{ fontSize: isPhone ? '11px' : '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5f5a55' }}>
                                                    {weekday}
                                                </span>
                                                {hasOrders && !isSkipped && (
                                                    <span title="Meals selected for this delivery">
                                                        <TruckIcon size={isPhone ? 18 : 20} />
                                                    </span>
                                                )}
                                                {isSkipped && (
                                                    <span style={{ fontFamily: FONTS.sans, fontSize: isPhone ? '10px' : '11px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#a08c74', textAlign: 'right', marginRight: canClose ? '22px' : 0 }}>
                                                        {isActuallySkipped ? 'Skipped' : <>No meals yet{stackNoMeals ? <br /> : ' '}<InfoHint text={NO_MEALS_HINT} size="10px" /></>}
                                                    </span>
                                                )}
                                            </div>

                                            <div style={{
                                                fontFamily: FONTS.serif,
                                                fontSize: isPhone ? '18px' : '21px',
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                color: COLORS.dark,
                                                textDecoration: isActuallySkipped ? 'line-through' : 'none',
                                            }}>
                                                {monthDay}
                                            </div>

                                            {lockDate && (
                                                <div style={{ fontSize: isPhone ? '11px' : '12px', color: '#4a4a4a', display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '4px' }}>
                                                    <span>Due</span>
                                                    <span>{formatLockDate(lockDate)}</span>
                                                </div>
                                            )}

                                            {isSelected && canEdit && (
                                                <button
                                                    type="button"
                                                    className="sf-tab-action"
                                                    onClick={onEdit}
                                                    style={{
                                                        alignSelf: 'center', fontFamily: 'inherit',
                                                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                                        color: 'rgb(241, 133, 25)', fontSize: '13px', fontWeight: 600,
                                                        minHeight: isPhone ? '28px' : undefined,
                                                        textDecoration: 'underline',
                                                    }}
                                                >
                                                    Change delivery
                                                </button>
                                            )}
                                            {isSelected && isActuallySkipped && !pastDeadline && onResume && (
                                                <button
                                                    type="button"
                                                    onClick={() => onResume(deliveryDate)}
                                                    className="sf-tab-action"
                                                    disabled={!!resumingDate}
                                                    style={{
                                                        alignSelf: 'center', fontFamily: 'inherit',
                                                        background: 'none', border: 'none', padding: 0, cursor: resumingDate ? 'default' : 'pointer',
                                                        color: 'rgb(241, 133, 25)', fontSize: '13px', fontWeight: 600,
                                                        minHeight: isPhone ? '28px' : undefined,
                                                        textDecoration: 'underline',
                                                    }}
                                                >
                                                    {resumingDate === deliveryDate ? 'Resuming…' : 'Resume delivery'}
                                                </button>
                                            )}
                                        </Tab>
                                    );
                                })}
                            </div>
                        ))}
                        <button
                            onClick={() => setShowAddPopup(true)}
                            aria-label="Add another delivery date"
                            title="Add another delivery date"
                            style={{
                                flex: '0 0 auto',
                                boxSizing: 'border-box',
                                width: `${cardWidth}px`,
                                marginTop: '8px',
                                marginBottom: '8px',
                                marginRight: isPhone ? '12px' : 0,
                                padding: isPhone ? '10px 12px' : '12px 16px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                border: '2px dashed #cdb89f',
                                borderRadius: '12px',
                                backgroundColor: 'transparent',
                                color: '#5a4630',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = CARD_BEIGE; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <span style={{ fontSize: '24px', fontWeight: 300, lineHeight: 1 }}>+</span>
                            <span style={{ fontFamily: FONTS.sans, fontSize: '13px', fontWeight: 700, color: COLORS.dark }}>Add delivery date</span>
                        </button>
                    </div>
                </div>
            </div>

            {panelBorderColor && (() => {
                const rootWidth = rootRef.current?.clientWidth ?? 0;
                const start = Math.max(0, tabRect.left + 2);
                const end = Math.min(rootWidth, tabRect.left + tabRect.width - 2);
                // Square a corner off when the open tab sits on it, so the tab's straight edge meets it cleanly.
                const leftRadius = start < 14 ? 0 : 12;
                const rightRadius = end > rootWidth - 14 ? 0 : 12;
                return (
                    <div style={{
                        position: 'relative', height: '12px', boxSizing: 'border-box',
                        border: `2px solid ${panelBorderColor}`, borderBottom: 'none',
                        borderRadius: `${leftRadius}px ${rightRadius}px 0 0`,
                    }}>
                        {end > start && (
                            <div style={{ position: 'absolute', top: '-2px', left: `${start - 2}px`, width: `${end - start}px`, height: '2px', background: PAGE_CREAM }} />
                        )}
                    </div>
                );
            })()}

            {showAddPopup && (
                <AddDeliveryModal
                    slots={getAddSlots(addableDates)}
                    onConfirm={(date, time) => {
                        handleAddDate(date);
                        onAddDelivery(date, time);
                        setShowAddPopup(false);
                    }}
                    onCancel={() => setShowAddPopup(false)}
                />
            )}

        </div>
    );
};

export default DeliveryDateTabs;
